import React, { useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import Box from '@mui/material/Box';

import reservationsAdapter, { IReservation } from '../adapters/reservationsAdapter';
import { IEmployee } from '../adapters/employeesAdapter';
import { IPropertyMgmtOrg } from '../adapters/propertyMgmtOrgsAdapter';
import useTranslator from '../../../utilities/hooks/useTranslator';
import useLocaleFormater from '../../../utilities/hooks/useLocaleFormater';
import { parseApiDate } from '../../../utilities/apiDates';
import LoadingState from '../../../components/LoadingState';
import { isApiConcurrencyError } from '../../../utilities/IApiConcurrencyError';
import useApiClient from '../../../utilities/hooks/useApiClient';

export interface ConfirmDialogProps {
    openInfo: { open: boolean, reservation: IReservation } | undefined,
    setOpenInfo: (openInfo: { open: boolean, reservation: IReservation } | undefined) => void,
    allEmployees: IEmployee[],
    propertyMgmtOrg: IPropertyMgmtOrg,
    safeSetViewLoadingState: (loadingState: LoadingState) => void,
    refreshSingleReservationInList: (reservation: IReservation) => void,
    safeSetUserMessageContent: (messageContent: string) => void
}

const ConfirmDialogDict = {
    confirmationDlgTitle: { en: 'Confirmation', fr: 'Confirmation' },
    confirmationDlgTextBeforeJobName: { en: 'Confirm employee on job ', fr: 'Confirmer pour l\'employé sur la job ' },
    confirmationDlgTextAfterJobName: { en: ' starting on {0} and that must be done before {1}.', fr: ' commençant le {0} et qui doit être terminée avant le {1}.' },
    confirmationDlgOkActionLbl: { en: 'Ok', fr: 'Ok' },
    confirmationDlgCancelActionLbl: { en: 'Cancel', fr: 'Annuler' },
    confirmationConcurrencyError: { en: 'Reservation for {0} has been modified by another user. Employee cannot be confirmed. Please refresh view and retry if needed.', fr: 'La réservation pour {0} a été modifiée par un autre utilisateurs. L\'employé ne peut être confirmer. SVP rafraîchir et réessayer si nécessaire.' },
    confirmationInternalError: { en: 'Internal error while trying to confirm employee {1} for {0}. Please refresh and retry if needed.', fr: 'Erreur interne lors de la confirmation de l\'employé {1} pour {0}. SVP rafraîchir et réessayer si nécessaire.' }
};

const ConfirmDialog = (props: ConfirmDialogProps) => {

    const translate = useTranslator();
    const formater = useLocaleFormater();
    const apiClient = useApiClient();

    const [dialogEmployeeIdToConfirm, setDialogEmployeeIdToConfirm] = useState<string>('');

    const cancelConfirmDialog = () => {

        setDialogEmployeeIdToConfirm('');
        props.setOpenInfo({ open: false, reservation: props.openInfo!.reservation });
    }

    const getFormattedJobDate = (jobDate: string) => formater.formatDate(parseApiDate(jobDate), { month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric' });

    const getEmployeesToConfirm = (reservation: IReservation) => reservation.cleaningJob.employees.filter((employee) => employee.status === 'Assigned');

    const getEmployee = (id: string) => props.allEmployees?.find((employee) => employee.id === id);

    const getFormattedEmployeeName = (employee: IEmployee) => employee.lastName + ' ' + employee.firstName;

    const getFormattedEmployeeNameWithId = (id: string) => {

        const employee = getEmployee(id);
        return employee ? getFormattedEmployeeName(employee) : '';
    };

    const confirmEmployee = () => {

        const employeeToConfirm = getEmployee(dialogEmployeeIdToConfirm);

        if (!employeeToConfirm)
            throw new Error('Internal error : Employee cannot be found.');

        const reservationToConfirm = props.openInfo!.reservation;

        setDialogEmployeeIdToConfirm('');
        props.setOpenInfo({ open: false, reservation: props.openInfo!.reservation });

        props.safeSetViewLoadingState(LoadingState.ModalWithVisibleContent);

        reservationsAdapter.confirmEmployeeOnCleaningJob(
            apiClient,
            props.propertyMgmtOrg?.id,
            reservationToConfirm.id,
            reservationToConfirm.aggregateTimeStamp,
            { targetEmployeeUniqueUserId: employeeToConfirm.uniqueUserId },
            true
        )
            .then((value) => {
                props.refreshSingleReservationInList(value)
            })
            .catch((error: any) => {

                if (isApiConcurrencyError(error))
                    props.safeSetUserMessageContent(translate(ConfirmDialogDict.confirmationConcurrencyError, [reservationToConfirm.rentalUnit.name]));
                else
                    props.safeSetUserMessageContent(translate(ConfirmDialogDict.confirmationInternalError, [reservationToConfirm.rentalUnit.name, getFormattedEmployeeName(employeeToConfirm)]));
            })
            .finally(() => props.safeSetViewLoadingState(LoadingState.None));
    };

    return <Dialog fullWidth onClose={() => { }} open={!!props.openInfo && props.openInfo.open} keepMounted data-cy='ConfirmDialog'>
        <DialogTitle>{translate(ConfirmDialogDict.confirmationDlgTitle)}</DialogTitle>
        <DialogContent dividers>
            <DialogContentText data-cy='ConfirmDialogText'>{translate(ConfirmDialogDict.confirmationDlgTextBeforeJobName)}<Box component='span' sx={{ fontWeight: 700 }}>{props.openInfo?.reservation.rentalUnit.name}</Box>{translate(ConfirmDialogDict.confirmationDlgTextAfterJobName, [props.openInfo ? getFormattedJobDate(props.openInfo.reservation.endDate) : '', props.openInfo ? getFormattedJobDate(props.openInfo.reservation.cleaningJob.mustBeCompletedBeforeDate) : ''])}</DialogContentText>
            <FormControl sx={{ mt: 2 }}>
                <RadioGroup
                    aria-labelledby="employees-to-confirm"
                    name="employees-to-confirm-rd"
                    onChange={(ev, value) => setDialogEmployeeIdToConfirm(value)}
                    value={dialogEmployeeIdToConfirm}>
                    {props.openInfo && getEmployeesToConfirm(props.openInfo.reservation).map((cleaningEmployee, index) => <FormControlLabel value={cleaningEmployee.id} key={cleaningEmployee.id} control={<Radio />} label={getFormattedEmployeeNameWithId(cleaningEmployee.id)} data-cy={'ConfirmDialogEmployee_' + index.toString()} />)}
                </RadioGroup>
            </FormControl>
        </DialogContent>
        <DialogActions>
            <Button autoFocus onClick={cancelConfirmDialog} data-cy='ConfirmDialogCancelAction'>{translate(ConfirmDialogDict.confirmationDlgCancelActionLbl)}</Button>
            <Button disabled={dialogEmployeeIdToConfirm === ''} onClick={confirmEmployee} data-cy='ConfirmDialogOkAction'>{translate(ConfirmDialogDict.confirmationDlgOkActionLbl)}</Button>
        </DialogActions>
    </Dialog>;
};

export default ConfirmDialog